/**
 * Telegram WebApp SDK init + utilities.
 *
 * Reads launch params from `window.Telegram.WebApp` (legacy API loaded via index.html
 * script tag — works inside Telegram clients). Verification of HMAC initData is the
 * backend's job; this module surfaces the params and triggers UI integrations
 * (theme, viewport, BackButton, MainButton).
 */
import type { z } from "zod";

export interface TgUser {
  readonly id: number;
  readonly firstName: string;
  readonly lastName: string | null;
  readonly username: string | null;
  readonly languageCode: string | null;
  readonly initial: string;
}

export interface TgLaunchContext {
  readonly inTelegram: boolean;
  readonly initData: string | null;
  readonly user: TgUser | null;
  readonly colorScheme: "dark" | "light";
  readonly platform: string;
}

interface RawWebApp {
  readonly initData: string;
  readonly initDataUnsafe: {
    readonly user?: {
      readonly id: number;
      readonly first_name: string;
      readonly last_name?: string;
      readonly username?: string;
      readonly language_code?: string;
    };
  };
  readonly colorScheme: "dark" | "light";
  readonly platform: string;
  /** Current visible height. Maps to CSS `var(--tg-viewport-height)`. */
  readonly viewportHeight?: number;
  /** Last stable visible height. Maps to CSS `var(--tg-viewport-stable-height)`. */
  readonly viewportStableHeight?: number;
  ready(): void;
  expand(): void;
  enableClosingConfirmation(): void;
  setHeaderColor(color: string): void;
  setBackgroundColor(color: string): void;
  onEvent(eventType: "viewportChanged", handler: (event: { isStateStable: boolean }) => void): void;
  offEvent(
    eventType: "viewportChanged",
    handler: (event: { isStateStable: boolean }) => void,
  ): void;
  HapticFeedback?: {
    impactOccurred(style: "light" | "medium" | "heavy" | "rigid" | "soft"): void;
    notificationOccurred(type: "error" | "success" | "warning"): void;
    selectionChanged(): void;
  };
  BackButton?: {
    show(): void;
    hide(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };
  MainButton?: {
    setText(text: string): void;
    show(): void;
    hide(): void;
    enable(): void;
    disable(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };
}

declare global {
  interface Window {
    Telegram?: {
      WebApp?: RawWebApp;
    };
  }
}

function getWebApp(): RawWebApp | null {
  if (typeof window === "undefined") return null;
  return window.Telegram?.WebApp ?? null;
}

/**
 * Mirror Telegram's reported viewport heights into CSS custom properties so
 * full-height layouts and the fixed bottom bar size against the *actual*
 * TG-controlled viewport, not the unreliable `100vh`/`100dvh` the in-app
 * WebView reports (notably iOS).
 *
 * Newer Telegram clients set `--tg-viewport-height` / `--tg-viewport-stable-height`
 * themselves; writing them here is idempotent and covers the legacy global API
 * (loaded via the index.html script tag) that does not. Subscribes to
 * `viewportChanged` to track keyboard / chrome resize. No teardown — this binds
 * once at app start and lives for the app's lifetime.
 */
function bindViewport(wa: RawWebApp): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const apply = (): void => {
    if (typeof wa.viewportHeight === "number" && wa.viewportHeight > 0) {
      root.style.setProperty("--tg-viewport-height", `${wa.viewportHeight}px`);
    }
    if (typeof wa.viewportStableHeight === "number" && wa.viewportStableHeight > 0) {
      root.style.setProperty("--tg-viewport-stable-height", `${wa.viewportStableHeight}px`);
    }
  };
  apply();
  try {
    wa.onEvent("viewportChanged", apply);
  } catch {
    // SDK may be unavailable when previewing outside Telegram. Silent.
  }
}

/** Initialize TG WebApp (expand, theme, header). Idempotent. Safe outside Telegram. */
export function initTelegram(): TgLaunchContext {
  const wa = getWebApp();
  if (!wa) {
    return {
      inTelegram: false,
      initData: null,
      user: null,
      colorScheme: "dark",
      platform: "web",
    };
  }
  try {
    wa.ready();
    wa.expand();
    wa.setHeaderColor("#0A1024");
    wa.setBackgroundColor("#0A1024");
  } catch {
    // SDK might be unavailable when previewing outside Telegram. Silent.
  }
  bindViewport(wa);
  const raw = wa.initDataUnsafe.user;
  const user: TgUser | null = raw
    ? {
        id: raw.id,
        firstName: raw.first_name,
        lastName: raw.last_name ?? null,
        username: raw.username ?? null,
        languageCode: raw.language_code ?? null,
        initial: (raw.first_name?.[0] ?? "?").toUpperCase(),
      }
    : null;
  return {
    inTelegram: true,
    initData: wa.initData,
    user,
    colorScheme: wa.colorScheme,
    platform: wa.platform,
  };
}

export function hapticImpact(style: "light" | "medium" | "heavy" = "light"): void {
  getWebApp()?.HapticFeedback?.impactOccurred(style);
}

export function hapticNotify(type: "success" | "warning" | "error"): void {
  getWebApp()?.HapticFeedback?.notificationOccurred(type);
}

export function hapticSelection(): void {
  getWebApp()?.HapticFeedback?.selectionChanged();
}

/**
 * Bind the TG WebApp MainButton to a label and click handler.
 * No-op outside Telegram. Returns a cleanup that hides the button and
 * removes the listener — call it from an effect cleanup.
 */
export function bindMainButton(
  label: string,
  onClick: () => void,
  options?: { readonly enabled?: boolean },
): () => void {
  const wa = getWebApp();
  const main = wa?.MainButton;
  if (!main) return () => undefined;
  main.setText(label);
  if (options?.enabled === false) main.disable();
  else main.enable();
  main.show();
  main.onClick(onClick);
  return () => {
    try {
      main.offClick(onClick);
      main.hide();
    } catch {
      // SDK may be torn down in dev hot-reload — ignore.
    }
  };
}

/**
 * Verify initData with backend. Backend should validate Telegram HMAC against bot token.
 * Returns parsed user payload on success, throws on failure.
 *
 * Hits POST /auth/telegram with body `{ init_data: string }` — must match
 * `TelegramVerifyRequest` in services/api/.../schemas/auth.py (extra="forbid").
 * For now, returns null when no initData (running outside TG).
 */
export async function verifyInitData<T>(
  initData: string | null,
  apiBase: string,
  schema: z.ZodType<T>,
): Promise<T | null> {
  if (!initData) return null;
  const res = await fetch(`${apiBase}/auth/telegram`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ init_data: initData }),
  });
  if (!res.ok) {
    throw new Error(`TG verify failed: ${res.status}`);
  }
  const data = (await res.json()) as unknown;
  return schema.parse(data);
}
